import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '../../components/Button';
import { ChoiceGroup, DayPicker } from '../../components/Choice';
import { Field, Input } from '../../components/Field';
import { useDb } from '../../db/hooks';
import { saveProfile, MODALITY_LABELS } from '../../data/profile';
import { saveSchedule } from '../../data/schedule';
import { inputValue, optionalNumber, optionalText, optionalTime } from '../../lib/forms';
import { formatDays, parseDays, WEEKDAYS, type Modality, type TreatmentSchedule } from '../../lib/schedule';

const schema = z.object({
  modality: z.enum(['in_center', 'home_hd'], { message: 'Choose how you dialyse.' }),
  days: z.array(z.enum(WEEKDAYS)).min(1, 'Pick at least one day.'),
  start_time: optionalTime,
  duration_min: optionalNumber(30, 720, 'Enter minutes between 30 and 720.'),
  location: optionalText(120)
});

type FormInput = z.input<typeof schema>;
type FormOutput = z.output<typeof schema>;

/** Edits the treatment schedule (and the profile's modality). */
export function ScheduleForm({
  current,
  modality,
  submitLabel = 'Save schedule',
  secondary,
  onSaved
}: {
  current: TreatmentSchedule | null;
  modality: Modality | null;
  submitLabel?: string;
  secondary?: React.ReactNode;
  onSaved?: () => void;
}) {
  const db = useDb();
  const {
    control,
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors, isSubmitting }
  } = useForm<FormInput, unknown, FormOutput>({
    resolver: zodResolver(schema),
    defaultValues: {
      modality: (current?.modality ?? modality ?? undefined) as Modality,
      days: parseDays(current?.days_of_week ?? 'mon,wed,fri'),
      start_time: inputValue(current?.start_time),
      duration_min: inputValue(current?.duration_min ?? 240),
      location: inputValue(current?.location)
    }
  });
  const chosenModality = watch('modality');

  async function submit(values: FormOutput) {
    try {
      await saveSchedule(db, current, {
        days_of_week: formatDays(values.days),
        start_time: values.start_time,
        duration_min: values.duration_min,
        location: values.location,
        modality: values.modality
      });
      await saveProfile(db, { modality: values.modality });
      onSaved?.();
    } catch (error) {
      setError('root', { message: error instanceof Error ? error.message : String(error) });
    }
  }

  return (
    <form onSubmit={handleSubmit(submit)} className="space-y-5" noValidate>
      <div>
        <span className="mb-2 block text-sm font-medium">How do you dialyse?</span>
        <Controller
          control={control}
          name="modality"
          render={({ field }) => (
            <ChoiceGroup
              name="modality"
              value={field.value ?? null}
              onChange={field.onChange}
              columns={2}
              options={[
                { value: 'in_center', label: MODALITY_LABELS.in_center, description: 'At a dialysis unit' },
                { value: 'home_hd', label: MODALITY_LABELS.home_hd, description: 'On your own machine' }
              ]}
            />
          )}
        />
        {errors.modality ? <p className="mt-1 text-xs text-clinical-danger">{errors.modality.message}</p> : null}
      </div>

      <div>
        <span className="mb-2 block text-sm font-medium">Which days?</span>
        <Controller control={control} name="days" render={({ field }) => <DayPicker label="Dialysis days" value={field.value} onChange={field.onChange} />} />
        {errors.days ? <p className="mt-1 text-xs text-clinical-danger">{errors.days.message}</p> : null}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Usual start time" error={errors.start_time?.message}>
          <Input type="time" {...register('start_time')} />
        </Field>
        <Field label="Length (minutes)" error={errors.duration_min?.message}>
          <Input inputMode="numeric" {...register('duration_min')} />
        </Field>
      </div>

      <Field label={chosenModality === 'home_hd' ? 'Where (optional)' : 'Dialysis unit'} error={errors.location?.message}>
        <Input {...register('location')} placeholder={chosenModality === 'home_hd' ? 'Home' : 'e.g. City Kidney Centre'} />
      </Field>

      {errors.root ? <p className="text-sm text-clinical-danger">{errors.root.message}</p> : null}

      <div className="flex flex-col gap-2">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving…' : submitLabel}
        </Button>
        {secondary}
      </div>
    </form>
  );
}
